import Alert from 'common/components/alert/Alert';
import Page from 'common/components/page/Page';
import { PageTemplates } from 'common/components/page/PageTemplates';
import AlertType from 'common/types/AlertType';
import React from 'react';
import { Button, Card } from 'react-bootstrap';
import SecurityQuestionInput from '../components/SecurityQuestionInput';
import CreateAccountInput from './CreateAccountInput';
import { CreateAccountState } from './CreateAccountReducer';

interface ICreateAccountPresenter {
  data: CreateAccountState;
  feedback: string[];
  loading: boolean;
  error: string | null;
  onChange: (key: string, value: string) => void;
  onQuestionChange: (num: '1' | '2' | '3') => (value: string) => void;
  onAnswerChange: (num: '1' | '2' | '3') => (value: string) => void;
  onSubmit: () => void;
}

const CreateAccountPresenter = (props: ICreateAccountPresenter) => {
  const { data, error, onQuestionChange, onAnswerChange, onSubmit } = props;

  return (
    <Page template={PageTemplates.centered}>
      {error && (
        <Alert type={AlertType.danger}>
          Something went wrong... {JSON.stringify(error)}
        </Alert>
      )}
      <Card>
        <Card.Body>
          <h3 className='mb-3'>Create an account</h3>
          <div className='alert alert-warning mb-3'>
            Please use your <strong>legal name</strong> when creating an
            account. First and last name must match how it's listed on your
            identification documents.
          </div>
          <CreateAccountInput
            inputType='input'
            label='Email Address'
            name='email'
            onChange={(value) => props.onChange('email', value)}
            autoComplete='email'
            fieldType='text'
            errors={props.feedback}
          />
          <CreateAccountInput
            inputType='input'
            label='First Name'
            name='first'
            onChange={(value) => props.onChange('first', value)}
            onKeyDown={(key) => key !== '@'}
            autoComplete='given-name'
            fieldType='text'
            errors={props.feedback}
          />
          <CreateAccountInput
            inputType='input'
            label='Last Name'
            name='last'
            onChange={(value) => props.onChange('last', value)}
            autoComplete='family-name'
            fieldType='text'
            errors={props.feedback}
          />
          <CreateAccountInput
            inputType='select'
            label='Suffix'
            name='suffix'
            autoComplete='honorific-suffix'
            selectOptions={[
              { value: 'II', text: 'II' },
              { value: 'III', text: 'III' },
              { value: 'IV', text: 'IV' },
              { value: 'V', text: 'V' },
              { value: 'Jr', text: 'Jr' },
              { value: 'Sr', text: 'Sr' },
            ]}
            onChange={(value) => props.onChange('suffix', value)}
            fieldType='text'
            errors={props.feedback}
          />
          <CreateAccountInput
            inputType='input'
            label='Password'
            name='password'
            onChange={(value) => props.onChange('password', value)}
            autoComplete='new-password'
            fieldType='password'
            errors={props.feedback}
            feedback={`Password must be 8-20 digits and contain at least one upper-case letter, one lower-case letter, one number, and one of the following characters: @#$%. It cannot contain any other special characters.`}
          />
          <CreateAccountInput
            inputType='input'
            label='Confirm Password'
            name='confirm'
            onChange={(value) => props.onChange('confirm', value)}
            autoComplete='new-password'
            fieldType='password'
            errors={props.feedback}
            feedback='Confirm must match.'
          />
          <SecurityQuestionInput
            index={1}
            onQuestionChange={onQuestionChange('1')}
            onAnswerChange={onAnswerChange('1')}
            blacklist={[data.security[2].question, data.security[3].question]}
            question={data.security[1].question}
            answer={data.security[1].answer}
            errors={props.feedback}
          />
          <SecurityQuestionInput
            index={2}
            onQuestionChange={onQuestionChange('2')}
            onAnswerChange={onAnswerChange('2')}
            blacklist={[data.security[1].question, data.security[3].question]}
            question={data.security[2].question}
            answer={data.security[2].answer}
            errors={props.feedback}
          />
          <SecurityQuestionInput
            index={3}
            onQuestionChange={onQuestionChange('3')}
            onAnswerChange={onAnswerChange('3')}
            blacklist={[data.security[1].question, data.security[2].question]}
            question={data.security[3].question}
            answer={data.security[3].answer}
            errors={props.feedback}
          />
          <Button
            color='primary'
            onClick={onSubmit}
            className='mt-2'
            disabled={props.loading}
          >
            Create Account
          </Button>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default CreateAccountPresenter;
